import { motion } from "framer-motion";
import { Quote, Flag, Cpu, Timer, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionTitle } from "@/components/Shared";

const disciplines = [
  { icon: Timer, t: "Karting Endurance", d: "A gruelling 4-hour race at Paul Ricard. Captain Ramando Hudson — one of EduKarting's first students in 2013 — with Christopher Bain Jr., Jashai Burrows & Giselle Liriano." },
  { icon: Flag, t: "Kart Slalom", d: "Maria Scott & Caden Burbridge threaded electric karts through a technical cone course, setting top-12 lap times." },
  { icon: Cpu, t: "Digital / Simulator", d: "Dominick Robinson became the first-ever eSports driver to represent The Bahamas at the Games." },
];

export const MSG2022Section = () => {
  return (
    <section data-testid="msg2022-section" className="relative overflow-hidden bg-[#050505] py-24 md:py-32">
      <span className="pointer-events-none absolute -right-6 top-10 select-none font-anton text-[20vw] leading-none text-white/[0.03] md:text-[12vw]">
        2022
      </span>
      <div className="relative mx-auto max-w-[1200px] px-5 md:px-10">
        <SectionTitle eyebrow="MARSEILLE, FRANCE · 2022" title="The FIA Motorsport Games" />

        {/* Magazine-style wrapped text */}
        <div className="mt-10 text-[1.05rem] leading-relaxed text-white/70">
          <Reveal>
            <figure className="mb-5 w-full overflow-hidden rounded-xl md:float-right md:mb-4 md:ml-8 md:w-[46%]">
              <img src="/msg2022/flagbearer.jpg" alt="Team Bahamas flag bearer at the 2022 FIA Motorsport Games" className="w-full object-cover" />
              <figcaption className="bg-obsidian px-4 py-2 text-xs tracking-wide text-white/50">
                Team Bahamas leads out at the Opening Ceremony, Marseille.
              </figcaption>
            </figure>
          </Reveal>

          <p>
            In 2022, EduKarting Bahamas took a team of young Bahamian drivers to Marseille, France, to
            represent The Bahamas on the world stage at the FIA Motorsport Games — the "Motorsport Olympics."
            It was the programme's international debut, and the culmination of years of hard work: overcoming
            financial hardship, hurricane-damaged equipment and the daunting logistics of getting a team across
            the Atlantic.
          </p>
          <p className="mt-4">
            Under the guidance of coaches Henry Beaudette, Craig "Cam" Camilleri and Wilfried Haslauer —
            and with tireless support from David McLaughlin, Susan Schauff and Carol Mason — Team Bahamas
            competed across three
            disciplines: Karting Endurance, Kart Slalom and Digital (Simulator) Racing. From the moment they
            walked out behind the flag, the smallest and least-funded nation in the field carried itself like a
            world leader.
          </p>

          <figure className="my-6 w-full overflow-hidden rounded-xl md:float-left md:mr-8 md:w-[46%]">
            <img src="/msg2022/stage.jpg" alt="Team Bahamas on stage at the 2022 FIA Motorsport Games" className="w-full object-cover" />
            <figcaption className="bg-obsidian px-4 py-2 text-xs tracking-wide text-white/50">
              The full squad on stage as "BAHAMAS" lights up the arena.
            </figcaption>
          </figure>

          <p>
            In the four-hour endurance race at Paul Ricard, our drivers slashed their lap times until their
            fastest was within 1.8 seconds of the quickest lap of the entire race. In the Slalom, our team sat
            inside the top 12 before narrow cone penalties. And in the Digital Cup, Dominick Robinson made
            history as the first eSports driver ever to fly the Bahamian flag at the Games.
          </p>
          <p className="mt-4">
            The results sheet shows modest finishes — but results don't tell the story. This was a team that
            didn't just compete; it inspired everyone watching, at the track and back home. It was living proof
            of what Bahamian youth can achieve with opportunity, discipline and belief.
          </p>
          <div className="clear-both" />
        </div>

        {/* Disciplines */}
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {disciplines.map((d, i) => (
            <Reveal key={d.t} delay={i * 0.1}>
              <div className="h-full rounded-xl border border-white/10 bg-obsidian p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-kbcyan/10 text-kbcyan">
                  <d.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-2xl tracking-wide text-white">{d.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{d.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Photo gallery — masonry so images tile with no gaps */}
        <div className="mt-12">
          <h3 className="mb-6 font-display text-3xl tracking-wide text-white">On The Ground In France</h3>
          <div className="gap-4 [column-fill:_balance] sm:columns-2 lg:columns-3">
            {[
              { src: "/msg2022/teamboard.jpg", cap: "Team Bahamas at the FIA Motorsport Games board" },
              { src: "/msg2022/graycliffhelmet.jpg", cap: "Flying the flag on the Graycliff Bahamas helmet" },
              { src: "/msg2022/kart9.jpg", cap: "Ramando Hudson with the #9 endurance kart" },
              { src: "/msg2022/walk.jpg", cap: "Carrying the flag through Marseille" },
              { src: "/msg2022/pitkart.jpg", cap: "With the #9 kart in the pit lane" },
              { src: "/msg2022/coach.jpg", cap: "En route in the team colours" },
              { src: "/msg2022/runway.jpg", cap: "Into the arena — a nation on the world stage" },
              { src: "/msg2022/trackhuddle.jpg", cap: "Trackside team huddle" },
            ].map((im) => (
              <figure key={im.src} className="group relative mb-4 block break-inside-avoid overflow-hidden rounded-xl">
                <img src={im.src} alt={im.cap} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80" />
                <figcaption className="absolute bottom-3 left-4 right-4 text-xs font-medium tracking-wide text-white/85">{im.cap}</figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* Pull quote */}
        <Reveal delay={0.1}>
          <blockquote className="mt-12 flex gap-4 rounded-xl border-l-4 border-kbyellow bg-obsidian p-8">
            <Quote className="h-8 w-8 flex-shrink-0 text-kbyellow" />
            <div>
              <p className="font-display text-2xl leading-snug tracking-wide text-white md:text-3xl">
                “Our team had done their country proud — and nobody had even turned a wheel yet. We made history.”
              </p>
              <footer className="mt-3 flex items-center gap-2 text-sm text-kbcyan">
                <Users className="h-4 w-4" /> Henry Beaudette · EduKarting Coach
              </footer>
            </div>
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
};

export default MSG2022Section;
